import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import styled, { useTheme } from 'styled-components';

import { publish, SubscriptionTopic } from '../../../../viewer/messaging/pubsub';
import { useDialog } from '../../../hooks/use-dialog';
import { Button } from '../../button/button';
import { Dialog } from '../../dialog/dialog';
import { FileSelectionButton } from '../../file-selection-button/file-selection-button';
import { Icon } from '../../icon/icon';
import { IconPanel } from '../../icon-panel/icon-panel';
import { InfoBox } from '../../info-box/info-box';
import { OverlayLoader } from '../../overlay-loader/overlay-loader';
import { Stack } from '../../stack/stack';
import { GeodataImagesContext } from '../geodata-images-state';
import { GeodataContext } from '../geodata-state';
import { AlignerContext } from './aligner-state';
import { getGeodataStatusIconContent } from './geodata-status-icon';
import { ClassicOptimizeConfiguation } from './start-process-dialog';
import { useGeodataCreateJobMutation } from './use-geodata-create-job-mutation';

const IntroductionPanel = () => {
  const { t } = useTranslation();

  return (
    <IntroductionPanelStyle>
      <IconPanel
        header={{ icon: { icon: ['fad', 'drone'], opacity: '50%', size: '4x' } }}
        responsiveness={{ compressed: false }}
      >
        {{
          title: t('aligner.addImages', { ns: 'cloudProcessing' }),
          info: t('aligner.introductionInfo', { ns: 'cloudProcessing' }),
        }}
      </IconPanel>
    </IntroductionPanelStyle>
  );
};

const IntroductionPanelStyle = styled.div`
  display: flex;
  padding: 1em;
`;

export const IntroductionDialog = React.memo(() => {
  const theme = useTheme();
  const { t } = useTranslation();
  const context = useContext(GeodataContext);
  const { state, setState, readyToQuickOptimize, showOrganizationAdminModules } =
    useContext(AlignerContext);
  const { geodata } = context;

  const { uploadImage } = useContext(GeodataImagesContext);
  const navigate = useNavigate();
  const { show, hide, render } = useDialog();

  const { createJobAsync, errorList, isPending } = useGeodataCreateJobMutation(() => {
    publish(SubscriptionTopic.ToastrMessage, {
      type: 'info',
      message: t('gcpPage.processDialog.processStartedMessage', { ns: 'cloudProcessing' }),
    });
  });

  // Initial state of the dialog.
  useEffect(() => {
    if (state === 'aligner') {
      hide();
    } else {
      show();
    }
  }, [hide, show, state]);

  const allowClose = useMemo(() => {
    return (
      state === 'finished' ||
      state === 'failed' ||
      state === 'align-photos-failed' ||
      state === 'process-pending' ||
      state === 'add-images'
    );
  }, [state]);

  const handleClose = useCallback(() => {
    if (allowClose) {
      hide();
    } else {
      void navigate(`/projects/${geodata.projectId}/geodata`);
    }
  }, [hide, navigate, geodata.projectId, allowClose]);

  const geodataStatusContent = useMemo(() => {
    return getGeodataStatusIconContent(geodata.status, theme, t, readyToQuickOptimize);
  }, [geodata.status, theme, t, readyToQuickOptimize]);

  const createOptimizeJob = useCallback(async () => {
    await createJobAsync({
      path: { geodataId: geodata.id },
      body: { type: 'optimize_classic' },
    });
    setState('process-pending');
  }, [createJobAsync, geodata.id, setState]);

  return (
    <>
      {render(
        <Dialog
          closeIcon={allowClose}
          context={(children) => (
            <GeodataContext.Provider value={context}>{children}</GeodataContext.Provider>
          )}
          maxHeight="expand"
          maxWidth={'90%'}
          onClose={handleClose}
        >
          {{
            header: t('aligner.title', { ns: 'cloudProcessing' }),
            content: (
              <OverlayLoader visible={isPending}>
                <Stack spacing={1}>
                  {state === 'add-images' && <IntroductionPanel />}
                  {state === 'optimize-legacy' && (
                    <InfoBox color="yellow">
                      {t('aligner.cloudProcessNeedsOptimize', { ns: 'cloudProcessing' })}
                    </InfoBox>
                  )}
                  {state === 'process-pending' && (
                    <InfoBox color="yellow">
                      <Stack spacing={0.5}>
                        {t('statuses.waitForProcessInfo', { ns: 'cloudProcessing' })}
                        <Stack alignItems="center" direction="row" spacing={0.5}>
                          <Icon icon={geodataStatusContent.icon} spin={geodataStatusContent.spin} />
                          <strong>{geodataStatusContent.text}</strong>
                        </Stack>
                      </Stack>
                    </InfoBox>
                  )}
                  {state === 'incompatible' && (
                    <InfoBox color="yellow">
                      {t('aligner.incompatibleCloudProcess', { ns: 'cloudProcessing' })}
                    </InfoBox>
                  )}
                  {state === 'finished' && (
                    <IconPanel
                      header={{
                        icon: {
                          icon: ['fad', 'check-circle'],
                          color: theme.color.green,
                          size: '5x',
                        },
                      }}
                      responsiveness={{ compressed: false }}
                    >
                      {{
                        title: geodataStatusContent.text,
                      }}
                    </IconPanel>
                  )}
                  {state === 'failed' && (
                    <IconPanel
                      header={{
                        icon: { icon: ['fad', 'circle-xmark'], color: theme.color.red, size: '5x' },
                      }}
                      responsiveness={{ compressed: false }}
                    >
                      {{
                        title: geodataStatusContent.text,
                      }}
                    </IconPanel>
                  )}
                  {state === 'align-photos-failed' && (
                    <>
                      <IconPanel
                        header={{
                          icon: {
                            icon: ['fad', 'circle-xmark'],
                            color: theme.color.red,
                            size: '5x',
                          },
                        }}
                        responsiveness={{ compressed: false }}
                      >
                        {{
                          title: geodataStatusContent.text,
                        }}
                      </IconPanel>
                      <InfoBox color="yellow" leftIcon={{ icon: 'info-circle' }}>
                        <Stack spacing={0.5}>
                          <span>
                            {t('aligner.alignPhotosFailed.previewUnavailable', {
                              ns: 'cloudProcessing',
                            })}
                          </span>
                          <span>
                            <Trans
                              components={{ bold: <strong /> }}
                              i18nKey="aligner.alignPhotosFailed.troubleshoot"
                              ns="cloudProcessing"
                              values={{
                                resetImagePositions: t('resetImagePositions', {
                                  ns: 'cloudProcessing',
                                }),
                              }}
                            />
                          </span>
                          <span>
                            {t('aligner.alignPhotosFailed.contactSupport', {
                              ns: 'cloudProcessing',
                            })}
                          </span>
                        </Stack>
                      </InfoBox>
                    </>
                  )}
                  {state === 'copy-existing' && (
                    <ClassicOptimizeConfiguation
                      onClick={(payload) => {
                        void createJobAsync({
                          path: { geodataId: geodata.id },
                          body: payload,
                        });
                      }}
                    />
                  )}
                  {errorList}
                </Stack>
              </OverlayLoader>
            ),
            footer: {
              left: showOrganizationAdminModules && state === 'add-images' && (
                <Button
                  color="secondary"
                  leftIcon={{ icon: ['fad', 'copy'] }}
                  variant="contained"
                  onClick={() => setState('copy-existing')}
                >
                  Kopiera från befintlig
                </Button>
              ),
              right: (
                <>
                  {state === 'add-images' && (
                    <FileSelectionButton
                      accept=".jpg,.jpeg"
                      color={'primary'}
                      disabled={isPending}
                      leftIcon={{ icon: ['fad', 'images'] }}
                      multiple={true}
                      text={t('aligner.addImages', { ns: 'cloudProcessing' })}
                      variant="contained"
                      onFilesSelected={(files) => {
                        void (async () => {
                          for (const file of files) {
                            await uploadImage(file);
                          }
                          hide();
                        })();
                      }}
                    />
                  )}
                  {state === 'optimize-legacy' && (
                    <Button
                      color="primary"
                      disabled={isPending}
                      loading={isPending}
                      variant="contained"
                      onClick={createOptimizeJob}
                    >
                      {t('optimize', { ns: 'cloudProcessing' })}
                    </Button>
                  )}
                  <Button disabled={isPending} variant="text" onClick={handleClose}>
                    {allowClose ? t('close', { ns: 'common' }) : t('back', { ns: 'common' })}
                  </Button>
                </>
              ),
            },
          }}
        </Dialog>,
      )}
    </>
  );
});

IntroductionDialog.displayName = 'IntroductionDialog';
